import React, { useMemo, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Safely get the publishable key
const getStripeKey = () => {
  const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
  if (!key) {
    console.error('Stripe public key is missing. Please check your environment variables.');
    return null;
  }
  
  // Check if we're in development mode
  const isDev = import.meta.env.DEV;
  if (isDev && key.startsWith('pk_live_')) {
    console.warn('Using live Stripe key in development mode. This may cause issues.');
  }
  
  return key;
};

// Initialize Stripe with error handling
const initializeStripe = () => {
  try {
    const key = getStripeKey();
    if (!key) return null;

    console.log('Initializing Stripe...');
    
    const stripePromise = loadStripe(key);

    if (!stripePromise) {
      throw new Error('Failed to create Stripe promise');
    }

    return stripePromise;
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    return null;
  }
};

const StripeWrapper = React.memo(({ children, amount }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('StripeWrapper mounted, initializing Stripe...');
    const stripe = initializeStripe();
    if (stripe) {
      console.log('Stripe initialized successfully');
      setStripePromise(stripe);
    } else {
      console.error('Failed to initialize Stripe');
      setError('Failed to initialize Stripe. Please try again later.');
    }
  }, []);

  const options = useMemo(() => ({
    mode: 'payment',
    currency: 'eur',
    amount: amount,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#4ADE80',
        colorBackground: '#1F2937',
        colorText: '#FFFFFF',
        colorDanger: '#EF4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '4px'
      },
    },
    paymentMethodTypes: ['card']
  }), [amount]);

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-50 rounded-lg">
        <p className="font-semibold">Error: {error}</p>
        <p className="text-sm mt-2">Please try refreshing the page or contact support if the issue persists.</p>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="text-gray-500 p-4 bg-gray-50 rounded-lg">
        <p>Loading payment system...</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
});

StripeWrapper.displayName = 'StripeWrapper';

export default StripeWrapper; 